import { getLaunch } from '@/lib/launch';

// ════════════════════════════════════════════════════════════════
// Скрининг пищевого поведения для онбординга (22.09.2026).
//
// Шесть вопросов по трём шкалам DEBQ (Ван Стрин): эмоциональное, внешнее
// и ограничительное пищевое поведение — по два вопроса на шкалу.
// Это НЕ валидированная короткая форма DEBQ, а скрининг. Поэтому результат
// ВСЕГДА идёт с подписью DISCLAIMER (требование Славы как психолога).
// Полный DEBQ (33 вопроса) — отдельно, внутри приложения, позже.
//
// Формулировки вопросов и текстов результата — здесь, в одном месте:
// Слава правит их сам, логика подсчёта от этого не меняется.
// ════════════════════════════════════════════════════════════════

export type EatingScale = 'emotional' | 'external' | 'restrained';
export type EatingStyle = EatingScale | 'balanced';

export type EatingProfile = {
  /** 1 — короткий скрининг (6 вопросов), 2 — полный DEBQ (33 вопроса) */
  version: 1 | 2;
  emotional: number;
  external: number;
  restrained: number;
  style: EatingStyle;
  answers: number[];
  taken_at: string;
};

export const DISCLAIMER = 'Это не диагноз, а то, на что нам стоит обратить внимание.';

export const EATING_QUESTIONS: { scale: EatingScale; text: string }[] = [
  { scale: 'emotional', text: 'Когда мне тревожно, грустно или я злюсь, меня тянет поесть' },
  { scale: 'emotional', text: 'После тяжёлого дня еда — первое, что помогает расслабиться' },
  { scale: 'external', text: 'Если вижу или чувствую запах вкусного, хочется съесть, даже когда я сыт(а)' },
  { scale: 'external', text: 'В компании или когда еда рядом, я съедаю больше, чем собирался(лась)' },
  { scale: 'restrained', text: 'Я стараюсь есть меньше, чем хочется, чтобы не набрать вес' },
  { scale: 'restrained', text: 'Я запрещаю себе продукты или сажусь на диету, а потом срываюсь' },
];

export const FREQUENCY_OPTIONS: { label: string; value: string }[] = [
  { label: 'Никогда', value: '1' },
  { label: 'Редко', value: '2' },
  { label: 'Иногда', value: '3' },
  { label: 'Часто', value: '4' },
  { label: 'Очень часто', value: '5' },
];

// Средние по шкалам DEBQ у людей с нормальным весом (docs/debq-ru.md).
// Решение Славы 22.09.2026: сравнивать не с абсолютным порогом «иногда» (3),
// а с нормой. Иначе эмоциональный стиль при норме 1,8 не ловился бы до 3,0.
export const NORMS: Record<EatingScale, number> = { restrained: 2.4, emotional: 1.8, external: 2.7 };

// Насколько балл должен превысить норму, чтобы сказать «стоит обратить внимание».
// В скрининге по 2 вопроса на шкалу балл шагает по 0,5, и один «редко» вместо
// «никогда» не должен давать результат. 0,5 — это минимум один ответ выше обычного
// по каждому из двух вопросов шкалы.
export const MARGIN = 0.5;

export function scoreEating(answers: number[]): EatingProfile {
  const mean = (scale: EatingScale) => {
    const vals = EATING_QUESTIONS.map((q, i) => (q.scale === scale ? answers[i] : null)).filter((v): v is number => v != null);
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  };
  return leadStyle({ emotional: mean('emotional'), external: mean('external'), restrained: mean('restrained') }, answers, 1);
}

// Ведущий стиль — тот, что СИЛЬНЕЕ ВСЕГО превышает свою норму (не меньше MARGIN).
// При равенстве порядок важности: эмоциональное → ограничительное → внешнее
// (первые два чаще стоят за срывами, с них и начинаем).
function leadStyle(scores: Record<EatingScale, number>, answers: number[], version: 1 | 2): EatingProfile {
  const order: EatingScale[] = ['emotional', 'restrained', 'external'];
  let lead: EatingScale | null = null;
  let best = MARGIN - 1e-9;
  for (const sc of order) {
    const excess = scores[sc] - NORMS[sc];
    if (excess > best) { best = excess; lead = sc; }
  }
  return { version, ...scores, style: lead ?? 'balanced', answers, taken_at: new Date().toISOString() };
}

export const STYLE_TEXT: Record<EatingStyle, { title: string; body: string; short: string }> = {
  emotional: {
    title: 'Эмоциональное пищевое поведение',
    short: 'еда как способ справиться с чувствами',
    body:
      'Похоже, еда для тебя часто — способ справиться с чувствами: тревогой, усталостью, одиночеством. ' +
      'Это очень частая история, и с ней можно работать мягко: учиться замечать, что на самом деле нужно в этот момент.',
  },
  external: {
    title: 'Внешнее пищевое поведение',
    short: 'еда «зовёт» сама — видом, запахом, компанией',
    body:
      'Похоже, тебя легко зовёт сама еда: запах, вид, компания, тарелка рядом. Так устроен мозг, это не слабость. ' +
      'Здесь помогают простые настройки среды, а не сила воли.',
  },
  restrained: {
    title: 'Ограничительное пищевое поведение',
    short: 'запреты, после которых организм берёт своё',
    body:
      'Похоже, ты часто себя ограничиваешь, а потом организм берёт своё. Жёсткие запреты — одна из самых частых причин срывов. ' +
      'Будем двигаться без запретов и диет.',
  },
  balanced: {
    title: 'Выраженного стиля нет',
    short: 'без ярко выраженного стиля',
    body:
      'По ответам ни один стиль не выражен — хорошая точка старта. Будем наблюдать, как ты ешь, и находить, что можно улучшить.',
  },
};

// Запуск по ссылке теста — лид-магнит (22.09.2026). Ссылка для продвижения:
//   https://t.me/kokorineatbot?startapp=test          (или test_<метка> для учёта источника)
// В вебе: https://slavahealth.ru/?start=test
// Новичок проходит тест ПЕРВЫМ, до вопросов про тело; кто уже в приложении —
// попадает на экран /test.
export function isTestLaunch(): boolean {
  return getLaunch()?.feature === 'test';
}

// ════════════════════════════════════════════════════════════════
// ПОЛНЫЙ DEBQ — 33 вопроса (лид-магнит, решение Славы 22.09.2026).
// Русский текст — широко используемый перевод (docs/debq-ru.md); формулировки
// оставлены как в опроснике (на «вы»), исправлены только очевидные опечатки:
// менять слова — значит менять сам инструмент.
// Ключ: 1–10 ограничительное, 11–23 эмоциогенное, 24–33 экстернальное,
// пункт 31 — обратный. Балл шкалы — среднее.
// ⚠️ Права на коммерческое использование полного DEBQ не проверены (решение Славы).
// ════════════════════════════════════════════════════════════════

export const FULL_QUESTIONS: { scale: EatingScale; text: string; reverse?: boolean }[] = [
  { scale: 'restrained', text: 'Если ваш вес начинает нарастать, вы едите меньше обычного?' },
  { scale: 'restrained', text: 'Стараетесь ли вы есть меньше, чем вам хотелось бы, во время обычного приёма пищи?' },
  { scale: 'restrained', text: 'Часто ли вы отказываетесь от еды и питья из-за того, что беспокоитесь о своём весе?' },
  { scale: 'restrained', text: 'Аккуратно ли вы контролируете количество съеденного?' },
  { scale: 'restrained', text: 'Выбираете ли вы пищу преднамеренно, чтобы похудеть?' },
  { scale: 'restrained', text: 'Если вы переели, будете ли вы на следующий день есть меньше?' },
  { scale: 'restrained', text: 'Стараетесь ли вы есть меньше, чтобы не поправиться?' },
  { scale: 'restrained', text: 'Часто ли вы стараетесь не есть между обычными приёмами пищи из-за того, что следите за своим весом?' },
  { scale: 'restrained', text: 'Часто ли вы стараетесь не есть вечером из-за того, что следите за своим весом?' },
  { scale: 'restrained', text: 'Имеет ли значение ваш вес, когда вы едите?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы раздражены?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вам нечего делать?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы подавлены или обескуражены?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вам одиноко?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вас кто-либо подвёл?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вам что-либо препятствует, встаёт на вашем пути, нарушаются ваши планы или что-то не удаётся?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы предчувствуете какую-либо неприятность?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы встревожены, озабочены или напряжены?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда «всё не так», «всё валится из рук»?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы испуганы?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы разочарованы, когда разрушены ваши надежды?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы взволнованы, расстроены?' },
  { scale: 'emotional', text: 'Возникает ли у вас желание есть, когда вы скучаете, утомлены, неспокойны?' },
  { scale: 'external', text: 'Едите ли вы больше, чем обычно, когда еда вкусная?' },
  { scale: 'external', text: 'Если еда хорошо выглядит и хорошо пахнет, едите ли вы больше обычного?' },
  { scale: 'external', text: 'Если вы видите вкусную пищу и чувствуете её запах, едите ли вы больше обычного?' },
  { scale: 'external', text: 'Если у вас есть что-либо вкусное, съедите ли вы это немедленно?' },
  { scale: 'external', text: 'Если вы проходите мимо булочной (кондитерской), хочется ли вам купить что-либо вкусное?' },
  { scale: 'external', text: 'Если вы проходите мимо закусочной или кафе, хочется ли вам купить что-либо вкусное?' },
  { scale: 'external', text: 'Если вы видите, как едят другие, появляется ли у вас желание есть?' },
  { scale: 'external', text: 'Можете ли вы остановиться, если едите что-либо вкусное?', reverse: true },
  { scale: 'external', text: 'Едите ли вы больше, чем обычно, в компании (когда едят другие)?' },
  { scale: 'external', text: 'Когда вы готовите пищу, часто ли вы её пробуете?' },
];

export function scoreFull(answers: number[]): EatingProfile {
  const mean = (scale: EatingScale) => {
    const vals = FULL_QUESTIONS
      .map((q, i) => (q.scale === scale ? (q.reverse ? 6 - answers[i] : answers[i]) : null))
      .filter((v): v is number => v != null);
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  };
  return leadStyle({ emotional: mean('emotional'), external: mean('external'), restrained: mean('restrained') }, answers, 2);
}

// «С чего начать» на экране результата (просьба Славы 22.09.2026): человек
// должен уйти не с ярлыком, а с понятным первым шагом. Три шага на стиль,
// каждый — действие на сегодня-завтра, и по возможности то, что уже есть
// в приложении (отметка дня, привычки, Relax).
export const STYLE_STEPS: Record<EatingStyle, string[]> = {
  emotional: [
    'Перед едой не по голоду — пауза в минуту. Спроси себя: я голоден или мне тяжело? Иногда хватает стакана воды и пары глубоких вдохов.',
    'Запиши три способа утешить себя без еды: прогулка, душ, звонок близкому. В разделе «Релакс» есть практика «Тяга к еде» на пару минут.',
    'Вечером отмечай настроение в «Отметке дня». Через неделю будет видно, в какие дни тянет к еде сильнее всего.',
  ],
  external: [
    'Убери еду с глаз: сладкое — в непрозрачные контейнеры и шкаф, на видное место — фрукты.',
    'Ешь с тарелки, а не из пачки, и без телефона — так проще заметить, что уже сыт. Это можно сделать привычкой на главной.',
    'Не иди в магазин и кафе голодным: перекус заранее снимает половину импульсивных покупок.',
  ],
  restrained: [
    'Сними один запрет: любимая еда разрешена в обычной порции. Чем строже запрет, тем сильнее потом тянет.',
    'Ешь регулярно — три приёма и перекус, без долгих голодных промежутков. Вечерние срывы чаще всего идут после «экономии» днём.',
    'Записывай еду без оценок «хорошо» или «плохо». Задача — видеть картину, а не наказывать себя.',
  ],
  balanced: [
    'Две недели записывай, что ешь, — так станет видно, что стоит поменять.',
    'Выбери одну привычку для здоровья на главной: вода, сон или прогулка.',
    'Спроси Дофи, что улучшить в рационе под твою цель.',
  ],
};

// Повтор теста — через 2 месяца (решение Славы 22.09.2026). Раньше динамику
// не увидеть: привычки питания меняются медленно, а частые повторы учат
// отвечать «как надо», а не как есть.
export const RETEST_DAYS = 60;

export type EatingTestRow = {
  id: string;
  version: 1 | 2;
  emotional: number;
  external: number;
  restrained: number;
  style: EatingStyle;
  taken_at: string;
};

export function daysSince(iso: string | undefined | null): number {
  if (!iso) return Infinity;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

export function retestDue(p: EatingProfile | null | undefined): boolean {
  return !!p && daysSince(p.taken_at) >= RETEST_DAYS;
}
